import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { requestForToken, onMessageListener } from "../firebase";
import "../css/Login.css";

const Login = () => {
  const navigate = useNavigate();
  // const server_url = localStorage.getItem("server_url");
  const server_url =
    localStorage.getItem("server_url") ||
    "https://mydigigarage.com/v1/";
  const server_folder = localStorage.getItem("server_folder");

  localStorage.setItem("server_url", server_url);

  const company_id = 2;

  // Check if already logged in
  useEffect(() => {
    const grage_id = localStorage.getItem("grage_id");
    const garageLoginNumber = localStorage.getItem("garageLoginNumber");
    if (garageLoginNumber) {
      navigate("/home/" + grage_id, { replace: true });
    }
  }, [navigate]);

  const [mode, setMode] = useState("request");
  const [formData, setFormData] = useState({
    user_mobile: "",
    otp: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [mobileVerified, setMobileVerified] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldAutoSubmit, setShouldAutoSubmit] = useState(true);
  const [hasChangedNumber, setHasChangedNumber] = useState(false);
  const [isWebOTPEnabled, setIsWebOTPEnabled] = useState(false);

  // Refs
  const mobileInputRef = useRef(null);
  const otpInputRef = useRef(null);
  const animationRef = useRef(null);
  const prevMobileValue = useRef("");
  const prevOtpValue = useRef("");
  const webOtpAbortController = useRef(null);

  // Listen for notifications
  useEffect(() => {
    const setupNotifications = async () => {
      try {
        const payload = await onMessageListener();
        if (payload) {
          showNotification(payload);
        }
      } catch (error) {
        console.error("Error setting up notification listener:", error);
      }
    };
    setupNotifications();
  }, []);

  // Timer for OTP resend
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Auto-submit logic for mobile
  useEffect(() => {
    if (mode === "request" && shouldAutoSubmit && !hasChangedNumber) {
      const currentMobile = formData.user_mobile;

      if (currentMobile.length < prevMobileValue.current.length) {
        stopAnimation();
      }

      if (currentMobile.length === 10 && !isAnimating) {
        if (validateMobile()) {
          startAnimation(() => handleRequestOtp(true));
        } else {
          stopAnimation();
        }
      }

      prevMobileValue.current = currentMobile;
    }
  }, [formData.user_mobile, mode, shouldAutoSubmit, hasChangedNumber]);

  // Auto-submit logic for OTP
  useEffect(() => {
    if (mode === "verify" && shouldAutoSubmit && !hasChangedNumber) {
      const currentOtp = formData.otp;

      if (currentOtp.length < prevOtpValue.current.length) {
        stopAnimation();
      }

      if (currentOtp.length === 6 && !isAnimating) {
        if (validateOtp()) {
          startAnimation(() => handleVerifyOtp(true));
        } else {
          stopAnimation();
        }
      }

      prevOtpValue.current = currentOtp;
    }
  }, [formData.otp, mode, shouldAutoSubmit, hasChangedNumber]);

  // Clean up animation
  useEffect(() => {
    return () => {
      stopAnimation();
    };
  }, []);

  // Web OTP API - FIXED VERSION
  useEffect(() => {
    // Check if Web OTP API is supported
    const checkWebOTPSupport = () => {
      const isSupported = "OTPCredential" in window;
      setIsWebOTPEnabled(isSupported);
      return isSupported;
    };

    // Only run Web OTP in verify mode and if auto-submit is enabled
    if (
      mode === "verify" &&
      shouldAutoSubmit &&
      !hasChangedNumber &&
      checkWebOTPSupport()
    ) {
      startWebOTPCapture();
    } else {
      // Clean up if Web OTP is not needed
      cleanupWebOTP();
    }

    return () => {
      cleanupWebOTP();
    };
  }, [mode, shouldAutoSubmit, hasChangedNumber]);

  const startWebOTPCapture = () => {
    // Abort any existing Web OTP request
    cleanupWebOTP();

    // Create new AbortController
    webOtpAbortController.current = new AbortController();

    // Set a timeout to auto-cancel after 2 minutes
    const timeoutId = setTimeout(() => {
      if (webOtpAbortController.current) {
        webOtpAbortController.current.abort();
      }
    }, 120000);

    navigator.credentials
      .get({
        otp: {
          transport: ["sms"],
        },
        signal: webOtpAbortController.current.signal,
      })
      .then((otp) => {
        clearTimeout(timeoutId);
        if (otp && otp.code) {
          setFormData((prev) => ({
            ...prev,
            otp: otp.code,
          }));

          // Automatically submit after 500ms
          setTimeout(() => {
            if (validateOtp()) {
              handleVerifyOtp(true);
            }
          }, 500);
        }
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        // Only log if it's not an expected abort
        if (err.name !== "AbortError") {
        }
      });
  };

  const cleanupWebOTP = () => {
    if (webOtpAbortController.current) {
      webOtpAbortController.current.abort();
      webOtpAbortController.current = null;
    }
  };

  const showNotification = (payload) => {
    const { title, body } = payload.notification;
    toast.success(`${title}: ${body}`, {
      duration: 5000,
      position: "top-right",
    });
  };

  const startAnimation = (callback) => {
    setIsAnimating(true);
    const duration = 1300;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed < duration) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        callback();
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  const stopAnimation = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    setIsAnimating(false);
  };

  const validateMobile = () => {
    const newErrors = {};
    if (!formData.user_mobile.trim()) {
      newErrors.user_mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.user_mobile)) {
      newErrors.user_mobile = "Please enter a valid 10-digit mobile number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOtp = () => {
    const newErrors = {};
    if (!formData.otp.trim()) {
      newErrors.otp = "OTP is required";
    } else if (!/^\d{6}$/.test(formData.otp)) {
      newErrors.otp = "Please enter a valid 6-digit OTP";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/\D/g, "");
    setFormData({
      ...formData,
      [name]: numericValue,
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const requestNotificationPermission = async () => {
    try {
      if (!("Notification" in window)) {
        return false;
      }

      // Check current permission
      let permission = Notification.permission;

      // If permission is already granted, return true
      if (permission === "granted") {
        return true;
      }

      // If permission is denied, don't ask again
      if (permission === "denied") {
        return false;
      }

      // Request permission with a timeout to prevent hanging
      const permissionPromise = Notification.requestPermission();
      const timeoutPromise = new Promise((resolve) =>
        setTimeout(() => resolve("timeout"), 5000)
      );

      const result = await Promise.race([permissionPromise, timeoutPromise]);

      if (result === "timeout") {
        return false;
      }

      permission = result;

      if (permission === "granted") {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  const handleRequestOtp = async (isAutoSubmit = false) => {
    if (!validateMobile()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const payload = {
        type: "request_login_otp",
        grage_number: formData.user_mobile,
        company_id: company_id,
      };

      const formDataToSend = new URLSearchParams();
      for (const key in payload) {
        formDataToSend.append(key, payload[key]);
      }

      const response = await axios.post(
        `${server_url}login.php`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (response.data.status === "1") {
        toast.success("OTP sent successfully!");
        setMode("verify");
        setMobileVerified(formData.user_mobile);
        setTimer(60);
        setFormData((prev) => ({ ...prev, otp: "" }));
        setHasChangedNumber(false);

        // Focus on OTP input
        setTimeout(() => {
          if (otpInputRef.current) {
            otpInputRef.current.focus();
          }
        }, 100);
      } else {
        toast.error(response.data.error || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Request OTP error:", error);
      toast.error("Unable to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (timer > 0) return;
    setIsLoading(true);

    try {
      const payload = {
        type: "resend_login_otp",
        grage_number: mobileVerified,
        company_id: company_id,
      };

      const formDataToSend = new URLSearchParams();
      for (const key in payload) {
        formDataToSend.append(key, payload[key]);
      }

      const response = await axios.post(
        `${server_url}login.php`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (response.data.status === "1") {
        toast.success("OTP resent successfully!");
        setTimer(60);

        // Restart Web OTP capture
        if (isWebOTPEnabled) {
          startWebOTPCapture();
        }
      } else {
        toast.error(response.data.error || "Failed to resend OTP");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      toast.error("Unable to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (isAutoSubmit = false) => {
    // Clean up Web OTP when manually verifying
    cleanupWebOTP();

    if (!validateOtp()) return;

    setIsLoading(true);

    try {
      const payload = {
        type: "verify_login_otp",
        grage_number: mobileVerified,
        otp: formData.otp,
        company_id: company_id,
      };

      const formDataToSend = new URLSearchParams();
      for (const key in payload) {
        formDataToSend.append(key, payload[key]);
      }

      const response = await axios.post(
        `${server_url}login.php`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      if (response.data.status === "1") {
        const dealerInfo = response.data.result[0];

        // Store auth token BEFORE checking user
        localStorage.setItem("userData", JSON.stringify(dealerInfo));
        localStorage.setItem("authToken", "authenticated");
        localStorage.setItem("grage_id", dealerInfo.grage_id);
        localStorage.setItem("garageLoginNumber", dealerInfo.grage_number);

        // Request notification permission in background (non-blocking)
        // Try to get FCM token immediately
        try {
          // Get notification permission
          if (
            "Notification" in window &&
            Notification.permission === "default"
          ) {
            await Notification.requestPermission();
          }

          // Attempt to get FCM token
          const token = await requestForToken(dealerInfo.grage_id, company_id);

          if (token) {
            localStorage.setItem("fcm_token", token);
          }
        } catch (fcmError) {
          console.error("FCM error during login:", fcmError);
          // Don't fail login if FCM fails
        }

        try {
          const form = new FormData();
          form.append("company_id", company_id);
          form.append("garage_id", dealerInfo.grage_id);
          form.append("version_code", "1.0");
          const storedToken = localStorage.getItem("fcm_token");
          if (storedToken) {
            form.append("web_token", storedToken);
          }

          const userResponse = await axios.post(
            `${server_url}check_user.php`,
            form,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          const res = userResponse.data;

          if (res.status !== "1") {
            // Even if check_user fails, user is still logged in
            console.warn("User validation failed, but login successful");
            toast.success("Login successful!");
            setTimeout(() => {
              navigate("/home/" + dealerInfo.grage_id, { replace: true });
            }, 500);
          }

          const user = res.result[0];
          const company = res.company_details[0];

          // Save user details
          localStorage.setItem("membership_hide_show", res.membership_hide_show);
          localStorage.setItem("grage_upi_id", user.grage_upi_id);
          localStorage.setItem("grage_referal_code", user.grage_referal_code);
          localStorage.setItem("grage_number", user.grage_number);
          localStorage.setItem("grage_name", user.grage_name);
          localStorage.setItem("grage_email", user.grage_email);
          localStorage.setItem("grage_address", user.grage_address);
          localStorage.setItem(
            "grage_contact_person_name",
            user.grage_contact_person_name
          );
          localStorage.setItem("grage_city_id", user.grage_city_id);
          localStorage.setItem("grage_state_id", user.grage_state_id);
          localStorage.setItem("grage_gst_no", user.grage_gst_no);

          // Save company details
          localStorage.setItem(
            "company_store_name",
            company.company_store_name
          );
          localStorage.setItem("company_favicon", company.company_favicon);
          localStorage.setItem(
            "company_footer_logo",
            company.company_footer_logo
          );
          localStorage.setItem(
            "company_header_logo",
            company.company_header_logo
          );
          localStorage.setItem("company_email", company.company_email);
          localStorage.setItem("company_mobile", company.company_mobile);
          localStorage.setItem("company_landline", company.company_landline);
          localStorage.setItem("company_address", company.company_address);
          localStorage.setItem(
            "company_terms_and_condition",
            company.company_terms_and_condition
          );
          localStorage.setItem(
            "company_privacy_policy",
            company.company_privacy_policy
          );
          localStorage.setItem("company_about_us", company.company_about_us);
          localStorage.setItem("company_website", company.company_website);

          toast.success("Login successful!");

          setTimeout(() => {
            navigate("/home/" + dealerInfo.grage_id, { replace: true });
          }, 500);
        } catch (err) {
          console.error("check_user error:", err);
          // Even if user details fail, user is still logged in
          toast.success("Login successful!");
          setTimeout(() => {
            navigate("/home/" + dealerInfo.grage_id, { replace: true });
          }, 500);
        }
      } else {
        toast.error(response.data.error || "Invalid OTP");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Verify OTP error:", error);
      toast.error("Unable to verify OTP. Please try again.");
      setIsLoading(false);
    }
  };

  const handleBackToMobile = () => {
    setMode("request");
    setFormData({
      user_mobile: mobileVerified,
      otp: "",
    });
    setErrors({});
    setTimer(0);
    setShouldAutoSubmit(false);
    setHasChangedNumber(true);
    stopAnimation();
    cleanupWebOTP();

    toast.success(
      "Auto-submit disabled. Please enter 10 digits and click Send OTP manually.",
      {
        duration: 3000,
        position: "top-right",
      }
    );
  };

  const toggleAutoSubmit = () => {
    setShouldAutoSubmit(!shouldAutoSubmit);
    toast.success(`Auto-submit ${!shouldAutoSubmit ? "enabled" : "disabled"}`, {
      duration: 2000,
      position: "top-right",
    });
  };

  const renderRequestForm = () => (
    <>
      <div className="login-logo">
        <h2>Welcome Back</h2>
        <p>Sign in with OTP</p>
      </div>

      <form
        className="login-form"
        onSubmit={(e) => {
          e.preventDefault();
          stopAnimation();
          handleRequestOtp(false);
        }}
      >
        <div className="form-group">
          <label className="form-label">Mobile Number</label>
          <div className="input-group">
            <div className="input-icon">
              <i className="fas fa-mobile-alt"></i>
            </div>
            <input
              ref={mobileInputRef}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              name="user_mobile"
              className={`form-input ${errors.user_mobile ? "error" : ""}`}
              placeholder="Enter 10-digit mobile number"
              value={formData.user_mobile}
              onChange={handleChange}
              maxLength="10"
              disabled={isLoading}
              autoComplete="tel"
              autoFocus
            />
          </div>
          {errors.user_mobile && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.user_mobile}
            </div>
          )}
        </div>

        <div className="button-container">
          <button
            type="submit"
            className={`login-button ${isLoading ? "loading" : ""} ${
              isAnimating ? "animating" : ""
            }`}
            disabled={isLoading || formData.user_mobile.length !== 10}
          >
            {isLoading && <span className="button-spinner"></span>}
            {isAnimating ? (
              <span className="button-content">
                <span className="button-text">Sending OTP</span>
                <div className="arrow-animation">
                  <span className="arrow-char">>>></span>
                </div>
              </span>
            ) : (
              "Send OTP"
            )}
          </button>
        </div>

        <div className="divider">
          <span>Or</span>
        </div>
        <div className="signup-link">
          Don't have an account?{" "}
          <Link to="/register" className="signup-text">
            Create Account
          </Link>
        </div>
      </form>
    </>
  );

  const renderVerifyForm = () => (
    <>
      <div className="login-logo">
        <h2>Verify OTP</h2>
        <p>Enter the code sent to your mobile</p>
      </div>

      <form
        className="login-form"
        onSubmit={(e) => {
          e.preventDefault();
          stopAnimation();
          handleVerifyOtp(false);
        }}
      >
        <div
          className="mobile-info"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#f0f7ff",
            padding: "12px 16px",
            borderRadius: "8px",
            marginBottom: "20px",
            fontSize: "14px",
            color: "#0352a1",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <i className="fas fa-check-circle" style={{ color: "#48bb78" }}></i>
            OTP sent to {mobileVerified}
          </div>
          <button
            type="button"
            onClick={handleBackToMobile}
            disabled={isLoading}
            style={{
              background: "none",
              border: "1px solid #0352a1",
              color: "#0352a1",
              padding: "4px 12px",
              borderRadius: "4px",
              fontSize: "12px",
              cursor: "pointer",
              transition: "all 0.3s",
            }}
            onMouseOver={(e) => (e.target.style.background = "#0352a1")}
            onMouseOut={(e) => (e.target.style.background = "none")}
          >
            Change
          </button>
        </div>

        <div className="form-group">
          <label className="form-label">OTP</label>
          <div className="input-group">
            <div className="input-icon">
              <i className="fas fa-key"></i>
            </div>
            <input
              ref={otpInputRef}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              name="otp"
              className={`form-input ${errors.otp ? "error" : ""}`}
              placeholder="Enter 6-digit OTP"
              value={formData.otp}
              onChange={handleChange}
              maxLength="6"
              disabled={isLoading}
              autoComplete="one-time-code"
              autoFocus
            />
          </div>
          {errors.otp && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.otp}
            </div>
          )}
        </div>

        <div className="button-container">
          <button
            type="submit"
            className={`login-button ${isLoading ? "loading" : ""} ${
              isAnimating ? "animating" : ""
            }`}
            disabled={isLoading || formData.otp.length !== 6}
          >
            {isLoading && <span className="button-spinner"></span>}
            {isAnimating ? (
              <span className="button-content">
                <span className="button-text">Verifying</span>
                <div className="arrow-animation">
                  <span className="arrow-char">>>></span>
                </div>
              </span>
            ) : (
              "Verify & Login"
            )}
          </button>
        </div>
        <p style={{ display: "none" }}>{server_url}</p>
        <div
          style={{
            textAlign: "end",
            margin: "10px 0 20px",
            fontSize: "14px",
          }}
        >
          {timer > 0 ? (
            <span style={{ color: "#718096" }}>Resend OTP in {timer}s</span>
          ) : (
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={isLoading || isAnimating}
              style={{
                background: "none",
                border: "none",
                color: "#0352a1",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                padding: "8px 16px",
                borderRadius: "4px",
                transition: "all 0.3s",
              }}
            >
              Resend OTP
            </button>
          )}
        </div>
      </form>
    </>
  );

  return (
    <div className="login-container">
      {/* Banner Section */}
      <div className="login-banner">
        <img
          src="https://mydigigarage.com/build_v1/img/2.png"
          alt="Garage Banner"
        />
      </div>

      {/* Form Section */}
      <div className="login-form-container">
        <div className="login-form-wrapper">
          {mode === "request" ? renderRequestForm() : renderVerifyForm()}
        </div>
      </div>
    </div>
  );
};

export default Login;
